import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlogPost, BlogFilters } from '../../types/blog';
import { fetchBlogPosts, fetchBlogTags } from '../../utils/blogUtils';
import BlogCard from './BlogCard';
import LoadingAnimation from '../LoadingAnimation';

interface BlogListProps {
  darkMode?: boolean;
}

const BlogList: React.FC<BlogListProps> = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BlogFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    loadBlogPosts();
    loadBlogTags();
    checkUserAuth();
  }, [filters]);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('DEBUG BlogList: Starting to load blog posts');
      const response = await fetchBlogPosts(filters);
      console.log('DEBUG BlogList: Received response:', response);
      console.log('DEBUG BlogList: Setting posts to:', response.items);
      setPosts(response.items);
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadBlogTags = async () => {
    try {
      const tags = await fetchBlogTags();
      setAvailableTags(tags);
    } catch (err) {
      console.error('Error loading blog tags:', err);
    }
  };

  const checkUserAuth = () => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, search: searchQuery || undefined }));
  };

  const handleTagFilter = (tag: string) => {
    const newTag = tag === selectedTag ? '' : tag;
    setSelectedTag(newTag);
    setFilters(prev => ({ ...prev, tag: newTag || undefined }));
  };

  const handlePostClick = (post: BlogPost) => {
    navigate(`/blog/${post.fields?.slug || 'unknown'}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingAnimation onComplete={() => {}} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-left mb-12">
          <img
            src="/nacme_logo.jpg"
            alt="NACME Logo"
            className="h-11 w-auto mb-4 drop-shadow"
            style={{ height: '2.8rem' }}
          />
          <h1 className="text-2xl font-normal mb-4">Searchlight Blog</h1>
          {/* Show admin link for authorized users */}
          {(userRole === 'recruiter' || userRole === 'admin') && (
            <button
              onClick={() => navigate('/blog-admin')}
              className="text-sm text-[#00A0DF] hover:text-[#00A0DF]/80 underline"
            >
              Blog Admin →
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="mb-16 space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search blog posts..."
              className={`flex-grow p-3 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              type="submit"
              className="px-6 py-3 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
            >
              Search
            </button>
          </form>

          {/* Tag Filters */}
          {availableTags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => handleTagFilter('')}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  selectedTag === ''
                    ? 'bg-[#00A0DF] text-white'
                    : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Posts
              </button>
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagFilter(tag)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    selectedTag === tag
                      ? 'bg-[#00A0DF] text-white'
                      : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
              <p className="font-bold">Error</p>
              <p>{error}</p>
              <button
                onClick={loadBlogPosts}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Blog Posts Grid */}
        {!error && (
          <>
            {console.log('DEBUG BlogList: Rendering posts, count:', posts.length)}
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No blog posts found. {searchQuery || selectedTag ? 'Try adjusting your filters.' : ''}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <BlogCard
                    key={post.sys.id}
                    post={post}
                    darkMode={darkMode}
                    onClick={() => handlePostClick(post)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Back to App Link */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/')}
            className={`px-6 py-2 rounded-lg transition-colors ${
              darkMode
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ← Back to NACME Searchlight
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogList;